import React, { useMemo } from 'react';

import {
  formatCountdown,
  resourceHourMinute,
  resourceTzLabel,
  resourceDayKey,
  agendaDayLabel,
  groupBySession,
} from './scheduleFormat';
import { HorizonChip } from './NextOpeningsPanel';

const ONE_HOUR_MS = 60 * 60 * 1000;

// Phone view of the upcoming schedule: one chronological agenda of gate
// closures grouped by day, with a live countdown headline for the next gate.
// Replaces the two side-by-side panels below lg.
export default function MobileScheduleAgenda({ openings = [], loading = false, now = Date.now() }) {
  const sessions = useMemo(() => groupBySession(openings), [openings]);

  const days = useMemo(() => {
    const byDay = new Map();
    for (const session of sessions) {
      const key = resourceDayKey(session.gateClosure);
      if (!byDay.has(key)) {
        byDay.set(key, { key, label: agendaDayLabel(session.gateClosure, now), sessions: [] });
      }
      byDay.get(key).sessions.push(session);
    }
    return [...byDay.values()];
  }, [sessions, now]);

  const nextSession = useMemo(
    () => sessions.find((s) => new Date(s.gateClosure).getTime() > now) || null,
    [sessions, now]
  );

  if (loading && sessions.length === 0) {
    return <div className="py-8 text-sm text-gray-500 dark:text-gray-400">Loading…</div>;
  }

  if (sessions.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200/70 bg-surface p-4 text-sm text-gray-500 dark:border-gray-600/70 dark:bg-gray-870 dark:text-gray-400">
        No openings in this window.
      </div>
    );
  }

  return (
    <div>
      {nextSession && (
        <div className="mb-4">
          <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">
            Next gate
          </div>
          <div className="text-xl font-medium tracking-tight text-gray-900 dark:text-gray-100">
            closes{' '}
            <span className="italic font-semibold text-brand-600 dark:text-brand-400">
              {formatCountdown(nextSession.gateClosure, now)}
            </span>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {days.map((day) => (
          <div key={day.key}>
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              {day.label}
            </div>
            <ol className="relative ml-1.5 border-l border-brand-100 dark:border-brand-900/50">
              {day.sessions.map((session) => {
                const gateMs = new Date(session.gateClosure).getTime();
                const past = gateMs <= now;
                const imminent = !past && gateMs - now <= ONE_HOUR_MS;
                return (
                  <li key={session.gateClosure} className="relative pb-3 pl-4 last:pb-1">
                    <span
                      aria-hidden="true"
                      className={`absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-surface dark:border-gray-870 ${
                        imminent
                          ? 'bg-brand-500'
                          : past
                          ? 'bg-gray-300 dark:bg-gray-600'
                          : 'bg-gray-400 dark:bg-gray-500'
                      }`}
                    />
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-mono text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                        {resourceHourMinute(session.gateClosure)}{' '}
                        {resourceTzLabel(session.gateClosure)}
                      </span>
                      <span
                        className={`text-xs font-semibold tabular-nums ${
                          imminent
                            ? 'text-brand-600 dark:text-brand-400'
                            : 'text-gray-600 dark:text-gray-300'
                        }`}
                      >
                        {past ? 'closed' : `closes ${formatCountdown(session.gateClosure, now)}`}
                      </span>
                    </div>
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Gate closes
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {session.items.map((item) => (
                        <HorizonChip
                          key={item.horizon_label}
                          label={item.horizon_label}
                          preview={!item.is_paid}
                        />
                      ))}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        ))}
      </div>
    </div>
  );
}
